import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  Container,
  InputAdornment,
  ListItemText,
  makeStyles,
  MenuItem,
  TextField,
  Typography,
} from "@material-ui/core";
import InputMask from "react-input-mask";
import PropTypes from "prop-types";
import Axios from "axios";
import { UserDataContext } from "../../Contexts/UserDataContext";
import clsx from "clsx";
import { v4 as uuidv4 } from "uuid";

const useStyles = makeStyles((theme) => ({
  flexRowContainer: { display: "flex", alignItems: "flex-end" },
  flexRowItem: { marginRight: "8px" },
  flexColContainer: {
    display: "flex",
    flexDirection: "column",
  },
  flexColItem: {
    margin: theme.spacing(1, 0, 0, 0),
  },
  active: {
    backgroundColor: theme.palette.success.main,
  },
  activeFocus: {
    backgroundColor: theme.palette.success.light,
  },
  waiting: {
    backgroundColor: theme.palette.warning.main,
  },
  waitingFocus: {
    backgroundColor: theme.palette.warning.main,
  },
  padBox: {
    padding: theme.spacing(1),
  },
  errorMessage: {
    color: theme.palette.error.main,
  },
}));

// input component custom must-catch props if not accepted as ...rest by underlying element:
// label
// name
// value
// onUpdate
// error

export const GenericNumericInputComponent = ({ value, onUpdate, ...rest }) => {
  const [strVal, setStrVal] = useState(value ? String(value) : "");

  return (
    <TextField
      value={strVal}
      onChange={(event) => {
        setStrVal(event.target.value);
        if (event.target.value === "") {
          onUpdate(event.target.name, undefined);
        } else if (!isNaN(event.target.value)) {
          onUpdate(event.target.name, Number(event.target.value));
        } else {
          onUpdate(event.target.name, event.target.value);
        }
      }}
      {...rest}
    />
  );
};
GenericNumericInputComponent.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
  onUpdate: PropTypes.func.isRequired,
  error: PropTypes.bool,
};

export const GenericCurrencyInputComponent = ({ value, onUpdate, ...rest }) => {
  const [stringValue, setStringValue] = useState("");

  useEffect(() => {
    if (value) {
      setStringValue(String(value));
    }
  }, []);

  const handleChange = (event) => {
    setStringValue(event.target.value);
    if (isNaN(event.target.value) || /\.$/.test(event.target.value))
      onUpdate(event.target.name, event.target.value);
    else if (event.target.value === "") onUpdate(event.target.name, undefined);
    else onUpdate(event.target.name, Number(event.target.value));
  };

  return (
    <TextField
      InputProps={{
        startAdornment: <InputAdornment position="start">$</InputAdornment>,
      }}
      value={stringValue}
      onChange={handleChange}
      {...rest}
    />
  );
};
GenericCurrencyInputComponent.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
  onUpdate: PropTypes.func.isRequired,
  error: PropTypes.bool,
};

export const GenericPercentageInputComponent = ({
  value,
  onUpdate,
  ...rest
}) => {
  const [stringValue, setStringValue] = useState(
    value ? String((value * 100).toFixed(2)) : ""
  );

  const handleChange = (event) => {
    // console.log(event.target.value);
    setStringValue(event.target.value);
    // if (isNaN(event.target.value) || /\.$/.test(event.target.value))
    if (isNaN(event.target.value))
      onUpdate(event.target.name, event.target.value);
    else if (event.target.value === "") onUpdate(event.target.name, undefined);
    else {
      onUpdate(
        event.target.name,
        parseFloat((Number(event.target.value) / 100).toFixed(5))
      );
    }
  };

  return (
    <TextField
      InputProps={{
        endAdornment: <InputAdornment position="end">%</InputAdornment>,
      }}
      value={stringValue}
      onChange={handleChange}
      {...rest}
    />
  );
};
GenericPercentageInputComponent.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  value: PropTypes.oneOf([PropTypes.number, PropTypes.string]).isRequired,
  onUpdate: PropTypes.func.isRequired,
  error: PropTypes.bool,
};

export const GenericDisabledTextInputComponent = ({ onUpdate, ...rest }) => (
  <TextField {...rest} disabled placeholder="Generated on creation" />
);
GenericDisabledTextInputComponent.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onUpdate: PropTypes.func.isRequired,
  error: PropTypes.bool,
};

export const GenericFreeTextInputComponent = ({ onUpdate, ...rest }) => (
  <TextField
    onChange={(event) => onUpdate(event.target.name, event.target.value)}
    {...rest}
  />
);
GenericFreeTextInputComponent.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onUpdate: PropTypes.func.isRequired,
  error: PropTypes.bool,
};

export const GenericDateInputComponent = ({
  name,
  value,
  onUpdate,
  ...rest
}) => {
  // not tested and not used
  const [dateString, setDateString] = useState(
    value ? value.toISOString().split("T")[0] : ""
  );

  const handleChange = (event) => {
    setDateString(event.target.value);
    const newDateString = event.target.value;
    const newDate = new Date(newDateString);
    if (!isNaN(newDate.valueOf())) {
      onUpdate(name, newDate);
    } else {
      onUpdate(name, undefined);
    }
  };

  return (
    <TextField
      type="date"
      value={dateString}
      onChange={handleChange}
      {...rest}
    />
  );
};
GenericDateInputComponent.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  value: PropTypes.instanceOf(Date),
  onUpdate: PropTypes.func.isRequired,
  error: PropTypes.bool,
};

export const GenericDateIsoStringInputComponent = ({
  value,
  onUpdate,
  ...rest
}) => {
  const [dateString, setDateString] = useState(
    value ? value.split("T")[0] : ""
  );
  const [timeString, setTimeString] = useState(
    value ? value.split("T")[1] : "00:00:00"
  );

  const handleChange = (event) => {
    setDateString(event.target.value);
    if (event.target.value === "") onUpdate(event.target.name, undefined);
    else onUpdate(event.target.name, `${event.target.value}T${timeString}`);
  };

  return (
    <TextField
      type="date"
      value={dateString}
      onChange={handleChange}
      {...rest}
    />
  );
};
GenericDateIsoStringInputComponent.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onUpdate: PropTypes.func.isRequired,
  error: PropTypes.bool,
};

export const SelectUserIdInputComponent = ({ onUpdate, value, ...rest }) => {
  const { userData } = useContext(UserDataContext);
  const [userList, setUserList] = useState([]);
  const [val, setVal] = useState(value ? String(value) : "");

  useEffect(() => {
    const getUsers = async () => {
      const request = {
        method: "GET",
        url: "/users/search?",
        headers: {
          "Content-Type": "application/json",
          Authorization: `bearer ${userData.token}`,
        },
      };
      try {
        const res = await Axios(request);
        setUserList(res.data);
      } catch (error) {
        console.log(error);
      }
    };

    getUsers();
  }, [userData.token]);

  const handleChange = (event) => {
    setVal(event.target.value);
    if (event.target.value === "") onUpdate(event.target.name, undefined);
    else onUpdate(event.target.name, Number(event.target.value));
  };

  return (
    <TextField select onChange={handleChange} value={val} {...rest}>
      <MenuItem value="">*Clear Selection*</MenuItem>
      {userList.map((user) => (
        <MenuItem key={user.id} value={String(user.id)}>
          {user.userName}
        </MenuItem>
      ))}
    </TextField>
  );
};
SelectUserIdInputComponent.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  value: PropTypes.number,
  onUpdate: PropTypes.func.isRequired,
  error: PropTypes.bool,
};

export const SelectOwnerDriverUserIdInputComponent = ({
  onUpdate,
  value,
  ...rest
}) => {
  const { userData } = useContext(UserDataContext);
  const [userList, setUserList] = useState([]);
  const [val, setVal] = useState(value ? String(value) : "");

  useEffect(() => {
    const getUsers = async () => {
      const request = {
        method: "GET",
        url: "/users/search?roleId=4",
        headers: {
          "Content-Type": "application/json",
          Authorization: `bearer ${userData.token}`,
        },
      };
      try {
        const res = await Axios(request);
        setUserList(res.data);
      } catch (error) {
        console.log(error);
      }
    };

    getUsers();
  }, [userData.token]);

  const handleChange = (event) => {
    setVal(event.target.value);
    if (event.target.value === "") onUpdate(event.target.name, undefined);
    else onUpdate(event.target.name, Number(event.target.value));
  };

  return (
    <TextField select onChange={handleChange} value={val} {...rest}>
      <MenuItem value="">*Clear Selection*</MenuItem>
      {userList.map((user) => (
        <MenuItem key={user.id} value={String(user.id)}>
          {user.userName}
        </MenuItem>
      ))}
    </TextField>
  );
};
SelectOwnerDriverUserIdInputComponent.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  value: PropTypes.number,
  onUpdate: PropTypes.func.isRequired,
  error: PropTypes.bool,
};

export const SelectTollAdminUserIdInputComponent = ({
  onUpdate,
  value,
  ...rest
}) => {
  const { userData } = useContext(UserDataContext);
  const [userList, setUserList] = useState([]);
  const [val, setVal] = useState(value ? String(value) : "");

  useEffect(() => {
    const getUsers = async () => {
      const request = {
        method: "GET",
        url: "/users/search?roleId=2",
        headers: {
          "Content-Type": "application/json",
          Authorization: `bearer ${userData.token}`,
        },
      };
      try {
        const res = await Axios(request);
        setUserList(res.data);
      } catch (error) {
        console.log(error);
      }
    };

    getUsers();
  }, [userData.token]);

  const handleChange = (event) => {
    setVal(event.target.value);
    if (event.target.value === "") onUpdate(event.target.name, undefined);
    else onUpdate(event.target.name, Number(event.target.value));
  };

  return (
    <TextField select onChange={handleChange} value={val} {...rest}>
      <MenuItem value="">*Clear Selection*</MenuItem>
      {userList.map((user) => (
        <MenuItem key={user.id} value={String(user.id)}>
          {user.userName}
        </MenuItem>
      ))}
    </TextField>
  );
};
SelectTollAdminUserIdInputComponent.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  value: PropTypes.number,
  onUpdate: PropTypes.func.isRequired,
  error: PropTypes.bool,
};

export const SelectTollManagerUserIdInputComponent = ({
  onUpdate,
  value,
  ...rest
}) => {
  const { userData } = useContext(UserDataContext);
  const [userList, setUserList] = useState([]);
  const [val, setVal] = useState(value ? String(value) : "");

  useEffect(() => {
    const getUsers = async () => {
      const request = {
        method: "GET",
        url: "/users/search?roleId=3",
        headers: {
          "Content-Type": "application/json",
          Authorization: `bearer ${userData.token}`,
        },
      };
      try {
        const res = await Axios(request);
        setUserList(res.data);
      } catch (error) {
        console.log(error);
      }
    };

    getUsers();
  }, [userData.token]);

  const handleChange = (event) => {
    setVal(event.target.value);
    if (event.target.value === "") onUpdate(event.target.name, undefined);
    else onUpdate(event.target.name, Number(event.target.value));
  };

  return (
    <TextField select onChange={handleChange} value={val} {...rest}>
      <MenuItem value="">*Clear Selection*</MenuItem>
      {userList.map((user) => (
        <MenuItem key={user.id} value={String(user.id)}>
          {user.userName}
        </MenuItem>
      ))}
    </TextField>
  );
};
SelectTollManagerUserIdInputComponent.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  value: PropTypes.number,
  onUpdate: PropTypes.func.isRequired,
  error: PropTypes.bool,
};

export const SelectActiveInputComponent = ({ onUpdate, ...rest }) => (
  <TextField
    select
    onChange={(event) => onUpdate(event.target.name, event.target.value)}
    {...rest}
  >
    <MenuItem value="">*Clear Selection*</MenuItem>
    <MenuItem value="true">Active</MenuItem>
    <MenuItem value="false">Inactive</MenuItem>
  </TextField>
);
SelectActiveInputComponent.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onUpdate: PropTypes.func.isRequired,
  error: PropTypes.bool,
};

export const SelectStateInputComponent = ({ value, onUpdate, ...rest }) => {
  const [val, setVal] = useState(value ? String(value) : "");

  const handleChange = (event) => {
    setVal(event.target.value);
    if (event.target.value === "") onUpdate(event.target.name, undefined);
    else onUpdate(event.target.name, Number(event.target.value));
  };

  return (
    <TextField select value={val} onChange={handleChange} {...rest}>
      <MenuItem value="">*Clear Selection*</MenuItem>
      <MenuItem value="1">Active</MenuItem>
      <MenuItem value="2">Waiting</MenuItem>
      <MenuItem value="3">Expired</MenuItem>
    </TextField>
  );
};
SelectStateInputComponent.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  value: PropTypes.number,
  onUpdate: PropTypes.func.isRequired,
  error: PropTypes.bool,
};

export const SelectRoleIdInputComponent = ({ value, onUpdate, ...rest }) => {
  const { userData } = useContext(UserDataContext);
  const [roleList, setRoleList] = useState([]);
  const [val, setVal] = useState(value ? String(value) : "");

  useEffect(() => {
    const fetchRoles = async () => {
      const request = {
        method: "GET",
        url: "/roles",
        headers: {
          "Content-Type": "application/json",
          Authorization: `bearer ${userData.token}`,
        },
      };
      try {
        const res = await Axios(request);
        setRoleList(res.data);
      } catch (error) {
        console.log(error);
      }
    };

    fetchRoles();
  }, []);

  const handleChange = (event) => {
    setVal(event.target.value);
    if (event.target.value === "") onUpdate(event.target.name, undefined);
    else onUpdate(event.target.name, Number(event.target.value));
  };

  return (
    <TextField select value={val} onChange={handleChange} {...rest}>
      <MenuItem value="">*Clear Selection*</MenuItem>
      {roleList.map((role) => (
        <MenuItem key={role.id} value={String(role.id)}>
          {role.roleDescription}
        </MenuItem>
      ))}
    </TextField>
  );
};
SelectRoleIdInputComponent.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  value: PropTypes.number,
  onUpdate: PropTypes.func.isRequired,
  error: PropTypes.bool,
};

export const SelectVendorIdInputComponent = ({ onUpdate, ...rest }) => {
  const [vendors, setVendors] = useState([]);
  const { userData } = useContext(UserDataContext);

  useEffect(() => {
    const getVendors = async () => {
      const request = {
        method: "GET",
        url: "/vendors",
        headers: {
          "Content-Type": "application/json",
          Authorization: `bearer ${userData.token}`,
        },
      };
      try {
        const res = await Axios(request);
        setVendors(res.data);
      } catch (error) {
        console.log(error);
      }
    };

    getVendors();
  }, [userData.token]);

  return (
    <TextField
      select
      onChange={(event) => onUpdate(event.target.name, event.target.value)}
      {...rest}
    >
      <MenuItem value="">*Clear Selection*</MenuItem>
      {vendors.map((vendor) => (
        <MenuItem key={vendor.id} value={vendor.id}>
          {vendor.tradingName}
        </MenuItem>
      ))}
    </TextField>
  );
};
SelectVendorIdInputComponent.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onUpdate: PropTypes.func.isRequired,
  error: PropTypes.bool,
};

export const SelectTruckIdInputComponent = ({ value, onUpdate, ...rest }) => {
  const [val, setVal] = useState(value ? String(value) : "");
  const [distinctList, setDistinctList] = useState([]);
  const { userData } = useContext(UserDataContext);

  useEffect(() => {
    const getTrucks = async () => {
      const request = {
        method: "GET",
        url: "/trucks/search",
        headers: {
          "Content-Type": "application/json",
          Authorization: `bearer ${userData.token}`,
        },
      };
      try {
        const res = await Axios(request);
        const idArray = res.data.map((truck) => truck.truckId);
        setDistinctList(
          res.data.filter(
            (truck, idx) => idArray.indexOf(truck.truckId) === idx
          )
        );
      } catch (error) {
        console.log(error);
      }
    };

    getTrucks();
  }, [userData.token]);

  const handleChange = (event) => {
    setVal(event.target.value);
    if (event.target.value === "") onUpdate(event.target.name, undefined);
    else onUpdate(event.target.name, Number(event.target.value));
  };

  return (
    <TextField select value={val} onChange={handleChange} {...rest}>
      <MenuItem value="">*Clear Selection*</MenuItem>
      {distinctList.map((truck) => (
        <MenuItem key={truck.truckId} value={truck.truckId}>
          {truck.truckId}
        </MenuItem>
      ))}
    </TextField>
  );
};
SelectTruckIdInputComponent.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  value: PropTypes.number,
  onUpdate: PropTypes.func.isRequired,
  error: PropTypes.bool,
};
export const SelectFixedOrRoundInputComponent = ({
  value,
  onUpdate,
  ...rest
}) => {
  const [strVal, setStrVal] = useState(value ? String(value) : "");

  const handleChange = (event) => {
    setStrVal(event.target.value);
    if (event.target.value === "") onUpdate(event.target.name, undefined);
    else onUpdate(event.target.name, Number(event.target.value));
  };

  return (
    <TextField select value={strVal} onChange={handleChange} {...rest}>
      <MenuItem value="">*Clear Selection*</MenuItem>
      <MenuItem value="3">Round Trip</MenuItem>
      <MenuItem value="4">Fixed Trip</MenuItem>
    </TextField>
  );
};
SelectFixedOrRoundInputComponent.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
  onUpdate: PropTypes.func.isRequired,
  error: PropTypes.bool,
};

export const GstNumberInputComponent = ({ onUpdate, ...rest }) => (
  <InputMask
    mask="999-999-999"
    maskChar="*"
    onChange={(event) => onUpdate(event.target.name, event.target.value)}
    {...rest}
    children={(inputProps) => <TextField {...inputProps} />}
  />
);

GstNumberInputComponent.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onUpdate: PropTypes.func.isRequired,
  error: PropTypes.bool,
};

export const SelectCostCentreInputComponent = ({
  value,
  onUpdate,
  ...rest
}) => {
  const [val, setVal] = useState(value);
  const { userData } = useContext(UserDataContext);
  const [branchList, setBranchList] = useState([]);
  const classes = useStyles();

  useEffect(() => {
    const fetchCostCentres = async () => {
      const requestActive = {
        method: "GET",
        url: "/branches/search?state=1",
        headers: {
          "Content-Type": "application/json",
          Authorization: `bearer ${userData.token}`,
        },
      };
      const requestWaiting = {
        method: "GET",
        url: "/branches/search?state=2",
        headers: {
          "Content-Type": "application/json",
          Authorization: `bearer ${userData.token}`,
        },
      };

      try {
        const [resActive, resWaiting] = await Promise.all([
          Axios(requestActive),
          Axios(requestWaiting),
        ]);
        setBranchList(
          [
            ...resActive.data.map((branch) => ({ ...branch, state: 1 })),
            ...resWaiting.data.map((branch) => ({ ...branch, state: 2 })),
          ].sort((a, b) => {
            if (a.branchName > b.branchName) return 1;
            if (a.branchName < b.branchName) return -1;
            return 0;
          })
        );
      } catch (error) {
        console.log(error);
      }
    };

    fetchCostCentres();
  }, []);

  const handleChange = (event) => {
    setVal(event.target.value);
    onUpdate(event.target.name, event.target.value);
  };

  return (
    <TextField select value={val} onChange={handleChange} {...rest}>
      <MenuItem value="">*Clear Selection*</MenuItem>
      {branchList.map((branch) => (
        <MenuItem
          // ListItemClasses={{
          //   root: branch.state === 1 ? classes.active : classes.waiting,
          //   focusVisible:
          //     branch.state === 1 ? classes.activeFocus : classes.waitingFocus,
          // }}
          key={branch.id}
          value={branch.lclLinehaulCostCentre}
        >
          <ListItemText>{branch.branchName}</ListItemText>
          <ListItemText>{branch.startDate.split("T")[0]}</ListItemText>
          <ListItemText>{(branch.endDate || "").split("T")[0]}</ListItemText>
        </MenuItem>
      ))}
    </TextField>
  );
};
SelectCostCentreInputComponent.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onUpdate: PropTypes.func.isRequired,
  error: PropTypes.bool,
};

export const SelectLclCostCentreInputComponent = ({
  value,
  onUpdate,
  ...rest
}) => {
  const [val, setVal] = useState(value);
  const { userData } = useContext(UserDataContext);
  const [branchList, setBranchList] = useState([]);

  useEffect(() => {
    const fetchCostCentres = async () => {
      const requestActive = {
        method: "GET",
        url: "/branches/search?state=1",
        headers: {
          "Content-Type": "application/json",
          Authorization: `bearer ${userData.token}`,
        },
      };
      const requestWaiting = {
        method: "GET",
        url: "/branches/search?state=2",
        headers: {
          "Content-Type": "application/json",
          Authorization: `bearer ${userData.token}`,
        },
      };

      try {
        const [resActive, resWaiting] = await Promise.all([
          Axios(requestActive),
          Axios(requestWaiting),
        ]);
        // get unique list of active + waiting branches, filter by duplicate branch name.
        const uniqueList = [
          ...resActive.data.map((branch) => ({ ...branch, state: 1 })),
          ...resWaiting.data.map((branch) => ({ ...branch, state: 2 })),
        ]
          .reduce(
            (acc, cur) =>
              acc.some((branch) => branch.branchName === cur.branchName)
                ? acc
                : [...acc, cur],
            []
          )
          .sort((a, b) => {
            if (a.branchName > b.branchName) return 1;
            if (a.branchName < b.branchName) return -1;
            return 0;
          });
        uniqueList.forEach((branch) => console.log(branch.branchName));
        setBranchList(uniqueList);
      } catch (error) {
        console.log(error);
      }
    };

    // [1, 1, 2, 2, 3, 3].reduce((acc, cur) => acc.some(n => n === cur) ? acc: acc + cur , [])

    fetchCostCentres();
  }, []);

  const handleChange = (event) => {
    setVal(event.target.value);
    onUpdate(event.target.name, event.target.value);
  };

  return (
    <TextField select value={val} onChange={handleChange} {...rest}>
      <MenuItem value="">*Clear Selection*</MenuItem>
      {branchList.map((branch) => (
        <MenuItem
          // ListItemClasses={{
          //   root: branch.state === 1 ? classes.active : classes.waiting,
          //   focusVisible:
          //     branch.state === 1 ? classes.activeFocus : classes.waitingFocus,
          // }}
          key={branch.id}
          value={branch.lclLinehaulCostCentre}
        >
          <ListItemText>{branch.branchName}</ListItemText>
        </MenuItem>
      ))}
    </TextField>
  );
};
SelectLclCostCentreInputComponent.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onUpdate: PropTypes.func.isRequired,
  error: PropTypes.bool,
};

export const SelectAndFilterTruckIdInputComponent = ({
  key,
  className,
  name,
  label,
  value,
  onUpdate,
  error,
  fullWidth,
  InputLabelProps,
  disabled,
  ...rest
}) => {
  const [val, setVal] = useState(value ? String(value) : "");
  const { userData } = useContext(UserDataContext);
  const [truckList, setTruckList] = useState([]);
  const [filter, setFilter] = useState("");
  const classes = useStyles();

  useEffect(() => {
    const getTrucks = async () => {
      const request = {
        method: "GET",
        url: "/trucks/search",
        headers: {
          "Content-Type": "application/json",
          Authorization: `bearer ${userData.token}`,
        },
      };
      try {
        const res = await Axios(request);
        const idArray = res.data.map((truck) => truck.truckId);
        setTruckList(
          res.data.filter(
            (truck, idx) => idArray.indexOf(truck.truckId) === idx
          )
        );
      } catch (error) {
        console.log(error);
      }
    };

    getTrucks();
  }, [userData.token]);

  const handleChange = (event) => {
    setVal(event.target.value);
    if (event.target.value === "") onUpdate(event.target.name, undefined);
    else onUpdate(event.target.name, Number(event.target.value));
  };

  return (
    <Box
      className={clsx(className, classes.flexRowContainer)}
      key={key}
      {...rest}
    >
      <TextField
        select
        label={label}
        name={name}
        value={val}
        onChange={handleChange}
        error={error}
        fullWidth={fullWidth}
        InputLabelProps={InputLabelProps}
        className={classes.flexRowItem}
        disabled={disabled}
      >
        <MenuItem value="">*Clear Selection*</MenuItem>
        {truckList
          .filter((truck) => new RegExp(filter).test(truck.truckId))
          .map((truck) => (
            <MenuItem key={truck.truckId} value={truck.truckId}>
              {truck.truckId}
            </MenuItem>
          ))}
      </TextField>
      <TextField
        value={filter}
        onChange={(event) => setFilter(event.target.value)}
        style={{ width: "240px" }}
        inputProps={{ style: { padding: "14px 14px 7px 14px" } }}
        variant="outlined"
        label="Filter truck list"
        InputLabelProps={InputLabelProps}
        size="small"
        disabled={disabled}
      />
    </Box>
  );
};
SelectAndFilterTruckIdInputComponent.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  value: PropTypes.number,
  onUpdate: PropTypes.func.isRequired,
  error: PropTypes.bool,
};

const SelectBranchByShortCode = ({ value, onUpdate, ...rest }) => {
  const [val, setVal] = useState(value);
  const { userData } = useContext(UserDataContext);
  const [branchList, setBranchList] = useState([]);

  useEffect(() => {
    const fetchCostCentres = async () => {
      const requestActive = {
        method: "GET",
        url: "/branches/search?state=1",
        headers: {
          "Content-Type": "application/json",
          Authorization: `bearer ${userData.token}`,
        },
      };
      const requestWaiting = {
        method: "GET",
        url: "/branches/search?state=2",
        headers: {
          "Content-Type": "application/json",
          Authorization: `bearer ${userData.token}`,
        },
      };

      try {
        const [resActive, resWaiting] = await Promise.all([
          Axios(requestActive),
          Axios(requestWaiting),
        ]);
        // get unique list of active + waiting branches, filter by duplicate shortCode.
        const uniqueList = [
          ...resActive.data.map((branch) => ({ ...branch, state: 1 })),
          ...resWaiting.data.map((branch) => ({ ...branch, state: 2 })),
        ]
          .reduce(
            (acc, cur) =>
              acc.some((branch) => branch.shortCode === cur.shortCode)
                ? acc
                : [...acc, cur],
            []
          )
          .sort((a, b) => {
            if (a.shortCode > b.shortCode) return 1;
            if (a.shortCode < b.shortCode) return -1;
            return 0;
          });
        // uniqueList.forEach((branch) => console.log(branch.shortCode));
        setBranchList(uniqueList);
      } catch (error) {
        console.log(error);
      }
    };

    fetchCostCentres();
  }, []);

  const handleChange = (event) => {
    setVal(event.target.value);
    onUpdate(event.target.name, event.target.value);
  };

  return (
    <TextField select value={val} onChange={handleChange} {...rest}>
      <MenuItem value="">*Clear Selection*</MenuItem>
      {branchList.map((branch) => (
        <MenuItem
          // ListItemClasses={{
          //   root: branch.state === 1 ? classes.active : classes.waiting,
          //   focusVisible:
          //     branch.state === 1 ? classes.activeFocus : classes.waitingFocus,
          // }}
          key={branch.id}
          value={branch.shortCode}
        >
          <ListItemText>{branch.shortCode}</ListItemText>
        </MenuItem>
      ))}
    </TextField>
  );
};

const SelectLclOrParcel = ({ value, onUpdate, ...rest }) => {
  const [val, setVal] = useState(value);

  return (
    <TextField
      select
      value={val}
      onChange={(event) => {
        setVal(event.target.value);
        onUpdate(event.target.name, event.target.value);
      }}
      {...rest}
    >
      <MenuItem value="">*Clear Selection*</MenuItem>
      <MenuItem value={false}>
        <ListItemText>LCL/Linehaul Cost Centre</ListItemText>
      </MenuItem>
      <MenuItem value={true}>
        <ListItemText>Parcel Cost Centre</ListItemText>
      </MenuItem>
    </TextField>
  );
};

const SelectCostCentreWeight = ({
  className,
  name,
  value,
  onUpdate,
  onDelete,
  disabled,
  error,
  ...rest
}) => {
  const classes = useStyles();
  const [val, setVal] = useState(value);
  const [valid, setValid] = useState({
    shortCode: false,
    isParcel: false,
    percentage: false,
  });

  const validate = {
    shortCode: (formData) => formData.shortCode !== "",
    isParcel: (formData) => formData.isParcel !== "",
    percentage: (formData) => !isNaN(formData.percentage),
  };

  const handleUpdate = (fieldName, newVal) => {
    // console.log(fieldName, newVal);
    let updated;
    updated = { ...val, [fieldName]: newVal };
    // console.log(updated);
    setVal(updated);
    setValid({ ...valid, [fieldName]: validate[fieldName](updated) });
    onUpdate(updated);
  };

  return (
    <Box
      border={1}
      borderRadius={4}
      borderColor={!error ? "primary.main" : "error.main"}
      // borderColor={"error.main"}
      className={clsx(classes.padBox, classes.flexColContainer, className)}
      {...rest}
    >
      <Box className={classes.flexRowContainer}>
        <SelectBranchByShortCode
          className={classes.flexRowItem}
          // className={className}
          name="shortCode"
          label="Branch"
          value={val.shortCode}
          onUpdate={handleUpdate}
          error={!valid.shortCode}
          fullWidth
          // InputLabelProps
          disabled={disabled}
        />
        <Button
          className={classes.flexRowItem}
          variant="outlined"
          color="secondary"
          onClick={() => onDelete(val)}
        >
          delete
        </Button>
      </Box>
      <SelectLclOrParcel
        // className={className}
        name="isParcel"
        label="Select Cost Centre"
        value={val.isParcel}
        onUpdate={handleUpdate}
        error={!valid.isParcel}
        fullWidth
        // InputLabelProps
        disabled={disabled}
      />
      <GenericPercentageInputComponent
        // className={className}
        name="percentage"
        label="Percentage"
        value={val.percentage}
        onUpdate={handleUpdate}
        error={!valid.percentage}
        fullWidth
        // InputLabelProps
        disabled={disabled}
      />
    </Box>
  );
};

export const CostCentreArrayInputComponent = ({
  value,
  onUpdate,
  error,
  className,
  InputLabelProps,
  name,
  ...rest
}) => {
  const classes = useStyles();
  const [val, setVal] = useState(value);
  const [valid, setValid] = useState(false);

  const handleCreate = () => {
    setVal([
      ...val,
      {
        id: uuidv4(),
        shortCode: "",
        isParcel: "",
        percentage: 0,
        costCentre: "",
      },
    ]);
  };

  const validatePercentageSum = (formData) => {
    const percentSum = Number(
      Number(
        formData.reduce((acc, cur) => acc + (cur.percentage || 0), 0)
      ).toFixed(4)
    );
    return percentSum === 1;
  };

  const handleChange = (newValue) => {
    // console.log(newValue);
    for (let i = 0; i < val.length; i++) {
      if (val[i].id === newValue.id) {
        // console.log("found matching id at index", i);
        const tempVal = [...val.slice(0, i), newValue, ...val.slice(i + 1)];
        setVal(tempVal);
        setValid(validatePercentageSum(tempVal));
        // console.log("setVal called");
        return;
      }
    }
  };

  const handleDelete = (newValue) => {
    for (let i = 0; i < val.length; i++) {
      if (val[i].id === newValue.id) {
        setVal([...val.slice(0, i), ...val.slice(i + 1)]);
        return;
      }
    }
  };

  useEffect(() => {
    console.log(val);
    onUpdate(name, val);
  }, [val]);

  return (
    <Box
      // border={1}
      // borderRadius={4}
      // borderColor={!error ? "primary.main" : "error.main"}
      {...rest}
    >
      <Typography className={classes.flexColItem} variant="body1">
        Select Cost Centres
      </Typography>

      {val.map((e) => (
        <SelectCostCentreWeight
          className={className}
          key={e.id}
          value={e}
          onUpdate={handleChange}
          onDelete={handleDelete}
        />
      ))}
      {error && !valid && (
        <Box className={classes.errorMessage}>
          <Typography className={clsx(classes.flexColItem)} variant="body1">
            Percentages must sum to 100
          </Typography>
        </Box>
      )}
      <Button
        variant="contained"
        color="primary"
        onClick={handleCreate}
        className={classes.flexColItem}
      >
        add
      </Button>
    </Box>
  );
};
